import {
  Condition,
  ConditionMode,
  CanvasStructure,
  HasConditionsStructure,
  SimpleAnalogStructure,
  SimpleBoolStructure,
  SimpleMultistateStructure,
  SimpleStructure,
  TileStructure,
} from './structure';
import {
  ALARM_COLOR_CRITICAL,
  ALARM_COLOR_WARNING,
  Appearance,
  ColorOption,
  TILE_APPEARANCE_DEFAULT_DECIMAL,
  TILE_APPEARANCE_DEFAULT_INCREMENT,
  TILE_APPEARANCE_DEFAULT_MULTIPLIER,
} from './appearance';
import {TileValueParser, TileValueType} from './tile-value-parser';
import {DisplayState} from './display-state';
import {Option, OptionGroup} from './option';
import {ObjectId} from './object.model';
import {UserId} from './user';
import {FacetValue, Tag, TagType, normalizeTag} from './tag';
import { CompareOperator } from '../constants/tile-config.constants';
import {TILE_MAX_DECIMAL_PLACES, TileSetPointDecimal} from './set-point';
import {DeviceId} from './device';
import {PROTOCOL_FORMULA} from './protocol';
import {sortByProfileOrder} from '../constants/tag-profiles.constants';



export const TILE_TYPE_SIMPLE_BOOL = 'SIMPLE_BOOL';

export const TILE_TYPE_SIMPLE_ANALOG = 'SIMPLE_ANALOG';

export const TILE_TYPE_SIMPLE_MULTISTATE = 'SIMPLE_MULTISTATE';
export const TILE_TYPE_SUPER_FOLDER = 'SUPER_FOLDER';

export const TILE_TYPE_SUPER_TREE = 'SUPER_TREE';

export const TILE_TYPE_SUPER_CANVAS = 'SUPER_CANVAS';

export const TILE_DEFAULT_WRITE_PRIORITY = 8;

/**
 * The TILE_CONDITIONS_QTY* constants define how many alert conditions each
 * tile type should have
 */
export const TILE_CONDITIONS_QTY_SIMPLE_BOOL = 1;
export const TILE_CONDITIONS_QTY_SIMPLE_ANALOG = 4;
export const TILE_CONDITIONS_QTY_SIMPLE_MULTISTATE = 1;

export const TILE_FORMAT_MULTICHOICE = 'multichoice';
export const TILE_FORMAT_NUMERIC = 'numeric';
export const TILE_FORMAT_TOGGLE = 'toggle';

export enum TileFormat {
  Empty = '',
  Toggle = 'toggle',
  Multichoice = 'multichoice',
  Numeric = 'numeric'
}

export type TileIdMap = { [tileId: TileId]: Tile };
export type TileType = '' |  typeof TILE_TYPE_SIMPLE_BOOL | typeof TILE_TYPE_SIMPLE_ANALOG | typeof TILE_TYPE_SIMPLE_MULTISTATE | typeof TILE_TYPE_SUPER_FOLDER | typeof TILE_TYPE_SUPER_TREE | typeof TILE_TYPE_SUPER_CANVAS;
export type TileId = ObjectId;
export type TileStateMap = { [value: number]: string };
export enum TileFolderDisplayMode {
  Default = 'default',
  DataPoints = 'dataPoints'
}

export function makeTileIdMap(tiles: Tile[]): TileIdMap {
  const map: TileIdMap = {};
  tiles.map((t) => {
    map[t.ID] = t;
  });
  return map;
}

export class Tile {
  ID: TileId;
  Type: TileType;
  Name: string;
  Owner: UserId;
  Enabled: boolean;
  Structure: TileStructure;
  Appearance: Appearance;
  Tags?: Tag[];

  get displayName(): string {
    return this.Appearance.DisplayName || this.Name;
  }

  get tags(): Tag[] {
    return (this.Tags ?? []).filter(t => t.Type === TagType.TAG);
  }

  get facets(): FacetValue[] {
    return (this.Tags ?? []).filter((t): t is FacetValue => t.Type === TagType.FACET && !!t.Value);
  }

  get facetValues(): string[] {
    return sortByProfileOrder(this.facets).map(f => f.Value);
  }

  constructor() {
  }


  static create(tileRaw: string | object): Tile {
    const newTile = new Tile();
    newTile.hydrate(tileRaw);
    return newTile;
  }

  /**
   * Factory method for creating new tiles (e.g., from CSV import or UI creation)
   * Sets a placeholder ID that will be replaced by the backend
   */
  static createNew(type: TileType, name: string): Tile {
    const tile = new Tile();
    tile.ID = '0'; // Placeholder ID for new/unsaved tiles
    tile.Type = type;
    tile.Name = name;
    tile.Enabled = true;
    return tile;
  }

  /**
   * Get default conditions for boolean/toggle tiles
   * Single source of truth for default alarm condition structure
   */
  static getDefaultBoolConditions(): Condition[] {
    return [
      {
        Enabled: false,
        Mode: ConditionMode.Critical,
        Comparison: { Operator: CompareOperator.Equal, Value: 0 },
        DisplayText: '',
        Icon: '',
        Delay: 0
      }
    ];
  }

  /**
   * Get default conditions for analog tiles
   * Single source of truth for default alarm condition structure
   * Order: [Low Critical, Low Warning, High Warning, High Critical]
   */
  static getDefaultAnalogConditions(): Condition[] {
    return [
      {
        Enabled: false,
        Mode: ConditionMode.Critical,
        Comparison: { Operator: CompareOperator.LessThan, Value: -1 },
        DisplayText: '',
        Icon: '',
        Delay: 0
      },
      {
        Enabled: false,
        Mode: ConditionMode.Warning,
        Comparison: { Operator: CompareOperator.LessThan, Value: 0 },
        DisplayText: '',
        Icon: '',
        Delay: 0
      },
      {
        Enabled: false,
        Mode: ConditionMode.Warning,
        Comparison: { Operator: CompareOperator.GreaterThan, Value: 1 },
        DisplayText: '',
        Icon: '',
        Delay: 0
      },
      {
        Enabled: false,
        Mode: ConditionMode.Critical,
        Comparison: { Operator: CompareOperator.GreaterThan, Value: 2 },
        DisplayText: '',
        Icon: '',
        Delay: 0
      }
    ];
  }


  hydrate(tileRaw: string | object): this {
    let obj: Tile;

    if (typeof tileRaw === 'string') {
      obj = JSON.parse(tileRaw);
    } else {
      obj = JSON.parse(JSON.stringify(tileRaw));
    }

    this.ID = obj.ID;
    this.Type = obj.Type;
    this.Name = obj.Name;
    this.Owner = obj.Owner;
    this.Enabled = obj.Enabled;
    // this.Structure = JSON.parse(JSON.stringify(obj.Structure));
    // this.Appearance = JSON.parse(JSON.stringify(obj.Appearance));
    this.Structure = obj.Structure;
    this.Appearance = obj.Appearance;
    this.Tags = obj.Tags
      ? [...obj.Tags].map(normalizeTag).sort((a, b) => a.Label.localeCompare(b.Label))
      : undefined;

    this.setDefaults();

    return this;
  }


  /**
   * As the structure of Tile evolves, certain new properties will be created that
   * have defaults that hadn't been set before. Calling this function whenever
   * instantiating a new instance will ensure those values get set if never set
   * before.
   */
  setDefaults(): this {
    const protocol = (this.Structure as SimpleStructure).Protocol;

    if (protocol === 'bacnet' && !this.Structure.Priority) {
      this.Structure.Priority = TILE_DEFAULT_WRITE_PRIORITY;
    }

    if (!this.Appearance) {
      this.Appearance = {} as Appearance;
    }

    if (typeof this.Appearance.Template === 'undefined') {
      this.Appearance.Template = '';
    }

    if (!this.Appearance.Icon) {
      this.Appearance.Icon = 'dot-circle'
    }

    if (!this.Appearance.Config) {
      this.Appearance.Config = {};
    }

    let structure;

    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
        structure = this.Structure as SimpleAnalogStructure;

        if (!structure.Conditions || structure.Conditions.length !== TILE_CONDITIONS_QTY_SIMPLE_ANALOG) {
          structure.Conditions = Tile.getDefaultAnalogConditions();
        }

        if (this.Appearance.Config && Object.keys(this.Appearance.Config).length === 0) {
          this.Appearance.Config = {
            Increment: TILE_APPEARANCE_DEFAULT_INCREMENT,
            Decimal: TILE_APPEARANCE_DEFAULT_DECIMAL,
            Multiplier: TILE_APPEARANCE_DEFAULT_MULTIPLIER,
          };
        }

        break;
      case TILE_TYPE_SIMPLE_BOOL:
        structure = this.Structure as SimpleBoolStructure;

        if (!structure.Conditions || structure.Conditions.length !== TILE_CONDITIONS_QTY_SIMPLE_BOOL) {
          structure.Conditions = Tile.getDefaultBoolConditions();
        }

        break;
      case TILE_TYPE_SIMPLE_MULTISTATE:
        structure = this.Structure as SimpleMultistateStructure;

        if (!structure.BuiltInStates) {
          structure.BuiltInStates = [];
        }

        if (!structure.DisplayStates || !structure.DisplayStates.length) {
          structure.DisplayStates = [];

          for (let i = 0; i < structure.BuiltInStates.length; i++) {
            structure.DisplayStates.push({
              CompareFunc: {
                Operator: CompareOperator.Equal,
                Value: protocol === 'bacnet' ? i + 1 : i, // bacnet multistate indexing starts with 1, not 0 :(
              },
              DisplayText: structure.BuiltInStates[i],
            });
          }
        }

        if (!structure.Conditions || structure.Conditions.length !== TILE_CONDITIONS_QTY_SIMPLE_MULTISTATE) {
          structure.Conditions = [
            {
              Enabled: false,
              Mode: ConditionMode.Critical,
              Comparison: {
                Operator: CompareOperator.Equal,
                Value: 0,
              },
              DisplayText: '',
              Icon: '',
              Alarm: false,
            },
          ];
        }

        break;
    }

    return this;
  }


  getStateLabels(): TileStateMap {
    let structure;

    switch (this.Type) {
      case TILE_TYPE_SIMPLE_BOOL:
        structure = this.Structure as SimpleBoolStructure;
        return {
          0: structure.DisplayFalse,
          1: structure.DisplayTrue,
        };
      case TILE_TYPE_SIMPLE_MULTISTATE:
        let states = {}; // default 0-index value here because of bacnet multistate

        structure = this.Structure as SimpleMultistateStructure;
        structure.DisplayStates.map((displayState: DisplayState) => {
          states[displayState.CompareFunc.Value] = displayState.DisplayText;
        });

        return states;
      default:
        throw new Error(`Tile type ${this.Type} does not support state labels`);
    }
  }


  /**
   * Returns the string for the units of the measurement this tile provides, if any.
   * If this type is a type without units, empty string is returned.
   */
  getUnitsLabel(): string | null {
    if (this.Type === TILE_TYPE_SIMPLE_ANALOG) {
      const structure: SimpleAnalogStructure = this.Structure as SimpleAnalogStructure;
      return structure.DisplayRawUnits || '';
    }

    return null;
  }


  getDeviceIds(): DeviceId[] {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return [(this.Structure as SimpleStructure).DeviceID];
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_FOLDER:
        return undefined;
    }
  }


  getFulfilledCondition(rawValue: TileValueType): Condition | null {
    const s = this.Structure as HasConditionsStructure;

    if (!s.Conditions) {
      return null;
    }

    switch (this.Type) {
      case TILE_TYPE_SIMPLE_BOOL: {
        let critical: Condition = s.Conditions[0];

        if (critical?.Enabled) {
          if (critical.Comparison.Value === 0 && rawValue === false) {
            return critical;
          } else if (critical.Comparison.Value === 1 && rawValue === true) {
            return critical;
          }
        }

        break;
      }
      case TILE_TYPE_SIMPLE_ANALOG: {
        let criticalLow: Condition;
        let criticalHigh: Condition;
        let warningLow: Condition;
        let warningHigh: Condition;

        for (let ci = 0; ci < s.Conditions.length; ci++) {
          const c = s.Conditions[ci];

          if (c.Mode === ConditionMode.Warning) {
            if (c.Comparison.Operator[0] === '<') {
              warningLow = c.Enabled ? c : undefined;
              continue;
            }

            if (c.Comparison.Operator[0] === '>') {
              warningHigh = c.Enabled ? c : undefined;
              continue;
            }
          }

          if (c.Mode === ConditionMode.Critical) {
            if (c.Comparison.Operator[0] === '<') {
              criticalLow = c.Enabled ? c : undefined;
              continue;
            }

            if (c.Comparison.Operator[0] === '>') {
              criticalHigh = c.Enabled ? c : undefined;
              continue;
            }
          }
        }

        let decimalPlaces: TileSetPointDecimal = this.Appearance.Config?.Decimal;

        if (decimalPlaces === undefined) {
          decimalPlaces = TILE_MAX_DECIMAL_PLACES;
        }

        let currentVal = Math.round(<number>rawValue * 10 ** decimalPlaces) / 10 ** decimalPlaces;

        if (criticalLow) {
          if (criticalLow.Comparison.Operator === '<=') {
            if (currentVal <= criticalLow.Comparison.Value) {
              return criticalLow;
            }
          } else {
            if (currentVal < criticalLow.Comparison.Value) {
              return criticalLow;
            }
          }
        }

        if (warningLow) {
          if (warningLow.Comparison.Operator === '<=') {
            if (currentVal <= warningLow.Comparison.Value) {
              return warningLow;
            }
          } else {
            if (currentVal < warningLow.Comparison.Value) {
              return warningLow;
            }
          }
        }

        if (criticalHigh) {
          if (criticalHigh.Comparison.Operator === '>=') {
            if (currentVal >= criticalHigh.Comparison.Value) {
              return criticalHigh;
            }
          } else {
            if (currentVal > criticalHigh.Comparison.Value) {
              return criticalHigh;
            }
          }
        }

        if (warningHigh) {
          if (warningHigh.Comparison.Operator === '>=') {
            if (currentVal >= warningHigh.Comparison.Value) {
              return warningHigh;
            }
          } else {
            if (currentVal > warningHigh.Comparison.Value) {
              return warningHigh;
            }
          }
        }

        break;
      }
      case TILE_TYPE_SIMPLE_MULTISTATE: {
        let critical: Condition = s.Conditions[0];

        if (critical?.Enabled) {
          if (critical.Comparison.Value === rawValue) {
            return critical;
          }
        }

        break;
      }
    }

    return null;
  }


  /**
   * Gets the alarm color for a given condition.
   * Returns ALARM_COLOR_CRITICAL for critical alarms, ALARM_COLOR_WARNING for warnings,
   * or null if no condition is provided.
   *
   * This is used by both regular tiles and mini-tiles to determine display color.
   *
   * @param condition - The fulfilled condition (from getFulfilledCondition) or null
   * @returns The alarm color (ALARM_COLOR_CRITICAL or ALARM_COLOR_WARNING) or null
   *
   * @example
   * const condition = tile.getFulfilledCondition(85.5);
   * const color = tile.getAlarmColor(condition);
   * // Returns ALARM_COLOR_WARNING if condition mode is Warning, ALARM_COLOR_CRITICAL if Critical
   */
  getAlarmColor(condition: Condition | null): ColorOption | null {
    if (!condition) {
      return null;
    }

    // For bool and multistate tiles, always show red (critical only)
    if (this.Type === TILE_TYPE_SIMPLE_BOOL || this.Type === TILE_TYPE_SIMPLE_MULTISTATE) {
      return ALARM_COLOR_CRITICAL;
    }

    // For analog tiles, check condition mode
    if (this.Type === TILE_TYPE_SIMPLE_ANALOG) {
      if (condition.Mode === ConditionMode.Warning) {
        return ALARM_COLOR_WARNING;
      } else if (condition.Mode === ConditionMode.Critical) {
        return ALARM_COLOR_CRITICAL;
      }
    }

    return null;
  }


  /**
   * Only works for analog tiles.
   *
   * @param preselectedDisplayValue - The menu option that should be
   *    pre-selected, based on the value that would appear in the menu
   *    to the end user.
   * @param optionCallbackFn - The function to which the numeric value
   *    for any given option will be supplied for execution whenever
   *    the user clicks on said option.
   * @param tileValueParser - Optional. If one is not supplied, this
   *    function will instantiate its own.
   */
  generateNumberOptions(
    preselectedDisplayValue: string,
    optionCallbackFn?: (number) => any,
    tileValueParser?: TileValueParser,
  ): OptionGroup {
    const options: Option[] = [];

    if (this.Type === TILE_TYPE_SIMPLE_ANALOG) {
      const appearance = this.Appearance;
      const increment = appearance?.Config?.Increment;

      if (!increment) {
        throw new Error('no increment set for tile for generateNumberOptions');
      }

      const min = appearance?.Config?.MinAllowed;
      const max = appearance?.Config?.MaxAllowed;

      if (min === undefined || max === undefined) {
        throw new Error('no min or max set for tile for generateNumberOptions');
      }

      let iteration = Math.ceil(min / increment) * increment;

      if (!tileValueParser) {
        tileValueParser = new TileValueParser(this);
      }

      while (iteration <= max) {
        const i = +iteration; // needed for this while loop's callback def below
        const displayValue = tileValueParser.getDisplayValue(iteration);

        options.push({
          id: displayValue,
          name: displayValue,
          callback: () => {
            if (optionCallbackFn) {
              optionCallbackFn(i);
            }
          },
          selected: preselectedDisplayValue === displayValue,
        });

        iteration += increment;
      }
    } else {
      throw new Error('unsupported tile type for generateNumberOptions');
    }

    return new OptionGroup(options);
  }


  /**
   * For when tiles can aggregate data from multiple hubs/devices, this function serves
   * to inform whether this tile is one of the types that will have such capabilities.
   */
  typeSupportsMultipleHubs(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return false;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return true;
    }

    throw new Error('tile type unknown');
  }


  typeSupportsTileHistory(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return true;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return false;
    }

    throw new Error('tile type unknown');
  }


  typeSupportsStackedTileHistory(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
        return true;
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return false;
    }

    throw new Error('tile type unknown');
  }


  isCanvasTile(): boolean {
    return this.Type === TILE_TYPE_SUPER_CANVAS;
  }


  getCanvasChildIds(): TileId[] {
    const points = (this.Structure as CanvasStructure)?.Points ?? [];
    return [...new Set(points.flatMap(p => p.TileIDs ?? []))];
  }


  isFormulaTile(): boolean {
    return (this.Structure as SimpleStructure)?.Protocol === PROTOCOL_FORMULA;
  }


  typeSupportsPolling(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return true;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return false;
    }

    throw new Error('tile type unknown');
  }


  typeSupportsReadWrite(): boolean {
    if (this.isFormulaTile()) {
      return false;
    }

    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return true;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return false;
    }

    throw new Error('tile type unknown');
  }


  typeSupportsCloning(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
      case TILE_TYPE_SUPER_CANVAS:
        return true;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
        return false;
    }

    throw new Error('tile type unknown');
  }


  typeSupportsAlarms(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return true;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return false;
    }

    throw new Error('tile type unknown');
  }


  typeSupportsPoints(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return true;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return false;
    }

    throw new Error('tile type unknown');
  }


  typeSupportsHavingDescendants(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return false;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return true;
    }

    throw new Error('tile type unknown');
  }


  typeSupportsBeingInFolder(): boolean {
    switch (this.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
      case TILE_TYPE_SIMPLE_BOOL:
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return true;
      case TILE_TYPE_SUPER_FOLDER:
      case TILE_TYPE_SUPER_TREE:
      case TILE_TYPE_SUPER_CANVAS:
        return false;
    }

    throw new Error('tile type unknown');
  }
}
